import Phaser from 'phaser';
import type { ChartNote, FoodDefinition } from '@/types';
import { foodTextureKey } from '@/data/foods';

const FOOD_DISPLAY_SIZE = 96;

/**
 * A pooled falling food sprite. Instances are created once by NoteManager and
 * reused for the lifetime of a song (`spawn`/`reset`) to avoid GC churn from
 * constantly allocating game objects during play.
 */
export class FoodNote extends Phaser.GameObjects.Container {
  chartNote: ChartNote | null = null;
  resolved = false;
  /** Smallest horizontal distance (px) between this note and the cat's catch-zone
   * center seen so far while overlapping, within the judgable timing window. null
   * until the cat first overlaps it. NoteManager grades Perfect/Good from this
   * (positional precision) rather than timing, and uses it to know whether the
   * note was ever caught at all before the window expires. */
  bestPositionError: number | null = null;
  /** currentSongTime - note.time (ms) at the moment bestPositionError was recorded;
   * carried through only for HitResult/debug display, not for grading. */
  bestPositionDiffMs = 0;
  /** Whether NoteManager has already told this lane's cat to play its anticipate
   * pose for this note, so it only fires once per note rather than every frame
   * it spends inside the timing window. */
  anticipationTriggered = false;

  private sprite: Phaser.GameObjects.Image;
  private bobSeed = 0;
  private spinSpeed = 0;
  private baseY = 0;

  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);
    this.sprite = scene.add.image(0, 0, foodTextureKey('iceCream'));
    this.sprite.setDisplaySize(FOOD_DISPLAY_SIZE, FOOD_DISPLAY_SIZE);
    this.add(this.sprite);
    scene.add.existing(this);
    this.setVisible(false);
  }

  spawn(chartNote: ChartNote, food: FoodDefinition, x: number): void {
    this.chartNote = chartNote;
    this.resolved = false;
    this.bestPositionError = null;
    this.bestPositionDiffMs = 0;
    this.anticipationTriggered = false;
    this.bobSeed = Math.random() * Math.PI * 2;
    this.spinSpeed = (Math.random() - 0.5) * 1.4;
    this.x = x;
    this.baseY = 0;
    this.setScale(1);
    this.setAlpha(1);
    this.rotation = 0;
    this.setVisible(true);
    this.sprite.setTexture(foodTextureKey(food.id));
  }

  /** Called every frame with the note's target Y (from RhythmEngine) plus a small
   * cosmetic bob/spin so falling food feels alive without affecting hit timing. */
  updateFall(targetY: number, timeMs: number): void {
    this.baseY = targetY;
    this.y = targetY + Math.sin(timeMs * 0.003 + this.bobSeed) * 5;
    this.rotation += this.spinSpeed * 0.016;
  }

  getWorldX(): number {
    return this.x;
  }

  getBaseY(): number {
    return this.baseY;
  }

  playCaught(onComplete: () => void): void {
    this.resolved = true;
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1, to: 1.6 },
      alpha: { from: 1, to: 0 },
      duration: 180,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.setVisible(false);
        onComplete();
      },
    });
  }

  playMissed(onComplete: () => void): void {
    this.resolved = true;
    this.scene.tweens.add({
      targets: this,
      y: this.y + 40,
      alpha: { from: 1, to: 0 },
      scale: { from: 1, to: 0.7 },
      duration: 220,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.setVisible(false);
        onComplete();
      },
    });
  }

  reset(): void {
    this.chartNote = null;
    this.resolved = false;
    this.setVisible(false);
    this.scene.tweens.killTweensOf(this);
  }
}
